// src/components/Adminpasswordchange/Adminpasswordchange.jsx
import React, { useState } from 'react';
import api from '../../utils/axios.secure';
import Menu from '../Menu/Menu';
import './css/adminpasswordchange.css';

// ───────────── 입력 규칙 (백엔드/프론트 동일) ─────────────
const MAX_ID_LEN = 28;
const MAX_PW_LEN = 32;

// 비밀번호 전체 허용 문자열 검사
const PW_ALLOWED_ALL = /^[A-Za-z0-9!@#$%^*()_+\-=]+$/;
// 비밀번호 한 글자 단위 검사
const PW_CHAR_ONE = /^[A-Za-z0-9!@#$%^*()_+\-=]$/;

// 서버 detail 코드 → 한글 메시지 매핑
const mapDetailMessage = (detail) => {
  if (!detail) return null;
  const code = String(detail).toUpperCase().replace(/\s+/g, '_');

  switch (code) {
    case 'INVALID_ADMIN_ID':
      return '관리자 계정 정보가 올바르지 않습니다.';
    case 'INVALID_NEW_PASSWORD':
      return '비밀번호는 10~32자, 공백X, (대/소/숫/특) 3종 이상, ID 포함 금지, 허용 특수문자만 사용';
    case 'WEAK_PASSWORD':
      return '비밀번호는 10~32자, 공백X, (대/소/숫/특) 3종 이상, ID 포함 금지, 허용 특수문자만 사용';
    case 'BAD_CURRENT_PASSWORD':
      return '현재 비밀번호가 일치하지 않습니다.';
    case 'ADMIN_NOT_FOUND':
      return '관리자 계정을 찾을 수 없습니다.';
    case 'UNAUTHORIZED':
      return '인증 정보가 유효하지 않습니다. 다시 로그인해주세요.';
    default:
      // 알 수 없는 코드는 서버가 준 detail을 그대로 보여줌
      return String(detail);
  }
};

export default function AdminPasswordChange() {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [loading, setLoading] = useState(false);

  // ───────────────────────────────
  //  한 글자씩 검사하는 입력 핸들러
  // ───────────────────────────────
  const handlePwInput = (setter, currentValue) => (e) => {
    const val = e.target.value;

    // 백스페이스 / 삭제는 허용
    if (val.length < currentValue.length) {
      setter(val);
      return;
    }

    const lastChar = val.slice(-1);

    // 한 글자 검사 (허용문자 아니면 입력 무시)
    if (!PW_CHAR_ONE.test(lastChar)) {
      return;
    }

    // 최종 적용 (길이 제한 포함)
    setter(val.slice(0, MAX_PW_LEN));
  };

  // ───────────────────────────────
  //  비밀번호 변경 요청
  // ───────────────────────────────
  const onSubmit = async (e) => {
    e.preventDefault();

    if (!currentPw) {
      alert('현재 비밀번호를 입력해주세요.');
      return;
    }

    if (!newPw || !newPw2) {
      alert('새 비밀번호를 입력해주세요.');
      return;
    }

    if (newPw !== newPw2) {
      alert('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    if (newPw.length < 8 || newPw.length > MAX_PW_LEN) {
      alert(`비밀번호는 8~${MAX_PW_LEN}자로 입력해주세요.`);
      return;
    }

    if (!PW_ALLOWED_ALL.test(newPw)) {
      alert('허용되지 않은 문자가 포함되어 있습니다.');
      return;
    }

    try {
      setLoading(true);

      const res = await api.post('/manager/user/change-password', {
        current_pw: currentPw,
        new_pw: newPw,
      });

      console.log('change-password res=', res);

      const ok =
        res?.data?.status === true ||
        res?.data?.data?.status === true;

      if (ok) {
        alert('비밀번호가 변경되었습니다. 다시 로그인해주세요.');

        try {
          // 로그아웃 API 호출 → httpOnly 쿠키 삭제 + 보안 이벤트 처리
          await api.post('/manager/user/logout');
        } catch (e) {
          console.error('logout after password change failed:', e);
        }

        window.location.href = '/login';
      } else {
        const detail =
          res?.data?.data?.detail ||
          res?.data?.data?.message ||
          res?.data?.data?.msg;

        console.log('change-password failed detail=', detail);
        const msg = mapDetailMessage(detail) || '비밀번호 변경에 실패했습니다.';
        alert(msg);
      }
    } catch (err) {
      console.error('change-password error:', err);

      const res = err.response;

      if (res && res.data) {
        // ⭐ 서버 래핑 구조까지 고려해서 detail 찾기
        const detail =
          res.data?.data?.detail ||  // { data: { detail: '...' } } 형태
          res.data.detail ||
          res.data.message ||
          res.data.msg ||
          res.data.error;

        console.log('change-password error detail from server =', detail);

        const msg = mapDetailMessage(detail);
        if (msg) {
          alert(msg);
          setLoading(false);
          return;
        }
      }

      // 여기까지 오면 정말로 알 수 없는 서버 쪽 문제일 확률이 큼
      alert('서버 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // ───────────────────────────────
  //  렌더링
  // ───────────────────────────────
  return (
    <Menu>
      <div className="contentWrap">
        <div className="content">
          <h2>관리자 비밀번호 변경</h2>

          <form onSubmit={onSubmit} className="pw-change-form">
            <label>현재 비밀번호</label>
            <input
              type="password"
              value={currentPw}
              onChange={handlePwInput(setCurrentPw, currentPw)}
              placeholder="현재 비밀번호"
              maxLength={MAX_PW_LEN}
              required
            />

            <label>새 비밀번호</label>
            <input
              type="password"
              value={newPw}
              onChange={handlePwInput(setNewPw, newPw)}
              placeholder="새 비밀번호"
              maxLength={MAX_PW_LEN}
              required
            />

            <label>새 비밀번호 확인</label>
            <input
              type="password"
              value={newPw2}
              onChange={handlePwInput(setNewPw2, newPw2)}
              placeholder="새 비밀번호 확인"
              maxLength={MAX_PW_LEN}
              required
            />

            <button disabled={loading} className="btn-primary">
              {loading ? '변경 중...' : '비밀번호 변경'}
            </button>
          </form>
        </div>
      </div>
    </Menu>
  );
}
